import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plus, Trash2, Edit2, Image, Check, RefreshCw, LogOut, Eye, 
  Calendar, FileText, BarChart3, Clock, CheckCircle2, AlertCircle,
  TrendingUp, BookOpen, Sparkles, Zap, Newspaper, Loader2,
  Wand2, Send, ChevronRight, Facebook, X, Save, ExternalLink, Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [activeTab, setActiveTab] = useState("posts");
  
  // AI Workflow State
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Building Inspections");
  const [customTopic, setCustomTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [batchTopics, setBatchTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingNews, setLoadingNews] = useState(false);
  
  // Stats State
  const [stats, setStats] = useState(null);
  
  // Schedule State
  const [scheduledPosts, setScheduledPosts] = useState([]);
  
  // Edit State
  const [editingPost, setEditingPost] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    "Building Inspections",
    "Pest & Termite",
    "Pool Safety",
    "Property Buying Tips",
    "Maintenance Advice",
    "Industry News"
  ];

  useEffect(() => {
    if (authenticated) {
      fetchPosts();
      fetchStats();
      fetchScheduledPosts();
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && activeTab === "ai-workflow") {
      fetchTopics();
      fetchNews();
    }
  }, [authenticated, activeTab, selectedCategory]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const authString = btoa(`${credentials.username}:${credentials.password}`);
      await axios.get(`${BACKEND_URL}/api/admin/blog`, {
        headers: { Authorization: `Basic ${authString}` }
      });
      
      sessionStorage.setItem("adminAuth", authString);
      setAuthenticated(true);
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    setAuthenticated(false);
    setCredentials({ username: "", password: "" });
  };

  const getAuthHeaders = () => {
    const authString = sessionStorage.getItem("adminAuth");
    return { Authorization: `Basic ${authString}` };
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/blog`, {
        headers: getAuthHeaders()
      });
      setPosts(response.data.posts);
    } catch (error) {
      console.error("Failed to fetch posts:", error);
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/blog/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchScheduledPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/auto-blog/schedule`, {
        headers: getAuthHeaders()
      });
      setScheduledPosts(response.data.scheduled_posts);
    } catch (error) {
      console.error("Failed to fetch scheduled posts:", error);
    }
  };

  const fetchTopics = async () => {
    setLoadingTopics(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/auto-blog/topics?category=${selectedCategory}`, {
        headers: getAuthHeaders()
      });
      setSuggestedTopics(response.data.topics);
    } catch (error) {
      console.error("Failed to fetch topics:", error);
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchNews = async () => {
    setLoadingNews(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/auto-blog/news?category=${selectedCategory}&limit=6`, {
        headers: getAuthHeaders()
      });
      setNewsItems(response.data.news);
    } catch (error) {
      console.error("Failed to fetch news:", error);
    } finally {
      setLoadingNews(false);
    }
  };

  const generateSinglePost = async (topic) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/auto-blog/generate`, {
        topic,
        category: selectedCategory
      }, { headers: getAuthHeaders() });
      
      toast.success(`Blog generated: ${response.data.post.title}`);
      fetchPosts();
      fetchStats();
    } catch (error) {
      console.error("Failed to generate post:", error);
      toast.error("Failed to generate blog post");
    } finally {
      setGenerating(false);
    }
  };

  const generateBatchPosts = async () => {
    if (batchTopics.length === 0) {
      toast.error("Add topics to the batch queue first");
      return;
    }
    
    setGenerating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/auto-blog/batch`, {
        topics: batchTopics,
        category: selectedCategory
      }, { headers: getAuthHeaders() });
      
      toast.success(`Generated ${response.data.generated} blog posts`);
      setBatchTopics([]);
      fetchPosts();
      fetchStats();
      fetchScheduledPosts();
    } catch (error) {
      console.error("Failed to generate batch:", error);
      toast.error("Failed to generate batch posts");
    } finally {
      setGenerating(false);
    }
  };

  const addToBatch = (topic) => {
    if (!batchTopics.includes(topic)) {
      setBatchTopics([...batchTopics, topic]);
      toast.success("Added to batch queue");
    }
  };

  const removeFromBatch = (topic) => {
    setBatchTopics(batchTopics.filter(t => t !== topic));
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/blog/${postId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Post deleted");
      fetchPosts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const publishPost = async (postId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/blog/${postId}/publish`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Post published!");
      fetchPosts();
      fetchStats();
      fetchScheduledPosts();
    } catch (error) {
      toast.error("Failed to publish post");
    }
  };

  const approveImage = async (postId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/blog/${postId}/approve-image`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Image approved");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to approve image");
    }
  };

  const regenerateImage = async (postId) => {
    try {
      toast.info("Regenerating image...");
      await axios.post(`${BACKEND_URL}/api/admin/blog/${postId}/regenerate-image`, {}, {
        headers: getAuthHeaders()
      });
      toast.success("Image regenerated");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to regenerate image");
    }
  };

  const uploadImage = async (postId, file) => {
    if (!file) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/blog/${postId}/upload-image`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success("Image uploaded successfully");
      
      // Update the editing post with new image
      if (editingPost && editingPost.id === postId) {
        setEditingPost({
          ...editingPost,
          featured_image_url: response.data.image_url,
          image_approved: true
        });
      }
      
      fetchPosts();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (e, postId) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(postId, file);
    }
  };

  const openEditDialog = async (postId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/blog/${postId}`, {
        headers: getAuthHeaders()
      });
      setEditingPost(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load post for editing");
    }
  };

  const savePost = async () => {
    if (!editingPost) return;
    
    try {
      await axios.put(`${BACKEND_URL}/api/admin/blog/${editingPost.id}`, {
        title: editingPost.title,
        content: editingPost.content,
        excerpt: editingPost.excerpt,
        category: editingPost.category,
        status: editingPost.status
      }, { headers: getAuthHeaders() });
      
      toast.success("Post saved");
      setEditDialogOpen(false);
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const retryFacebook = async (postId) => {
    try {
      toast.info("Posting to Facebook...");
      const response = await axios.post(`${BACKEND_URL}/api/admin/blog/${postId}/facebook/retry`, {}, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        toast.success("Posted to Facebook!");
      } else {
        toast.error(response.data.error || "Failed to post to Facebook");
      }
      fetchPosts();
    } catch (error) {
      toast.error("Failed to post to Facebook");
    }
  };

  // Login Form
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full"
        >
          <h1 className="text-2xl font-bold text-slate-900 mb-6 text-center">Blog Admin</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">Email</Label>
              <Input
                id="username"
                type="email"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Login
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-6 h-6 text-primary" />
            <h1 className="text-xl font-bold text-slate-900">Blog Admin</h1>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="posts" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Posts</span>
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Schedule</span>
            </TabsTrigger>
            <TabsTrigger value="ai-workflow" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">AI Workflow</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">All Blog Posts</h2>
              <Button onClick={fetchPosts} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Title</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Category</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Image</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Facebook</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {posts.map((post) => (
                        <tr key={post.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {post.auto_generated && (
                                <Sparkles className="w-4 h-4 text-amber-500" title="AI Generated" />
                              )}
                              <span className="text-sm font-medium text-slate-900 line-clamp-1">{post.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-600">{post.category}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              post.status === "published" ? "bg-green-100 text-green-700" :
                              post.status === "scheduled" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {post.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {post.featured_image_url ? (
                              <div className="flex items-center gap-2">
                                <img loading="lazy" src={post.featured_image_url.startsWith('/api') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url} alt="" className="w-10 h-10 rounded object-cover" />
                                {post.image_approved ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-500" title="Approved" />
                                ) : (
                                  <div className="flex gap-1">
                                    <button onClick={() => approveImage(post.id)} className="text-green-600 hover:text-green-700" title="Approve">
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => regenerateImage(post.id)} className="text-blue-600 hover:text-blue-700" title="Regenerate">
                                      <RefreshCw className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">No image</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {post.facebook_post_id ? (
                              <span className="text-green-600 flex items-center gap-1">
                                <Facebook className="w-4 h-4" />
                                <CheckCircle2 className="w-3 h-3" />
                              </span>
                            ) : post.status === "published" ? (
                              <button onClick={() => retryFacebook(post.id)} className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                <Facebook className="w-4 h-4" />
                                <span className="text-xs">Post</span>
                              </button>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button onClick={() => openEditDialog(post.id)} className="text-slate-600 hover:text-slate-900" title="Edit">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              {post.status !== "published" && (
                                <button onClick={() => publishPost(post.id)} className="text-green-600 hover:text-green-700" title="Publish">
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => deletePost(post.id)} className="text-red-600 hover:text-red-700" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {posts.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                            No blog posts yet. Use the AI Workflow tab to generate some!
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Upcoming Scheduled */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Upcoming Scheduled Posts
                </h3>
                {scheduledPosts.length > 0 ? (
                  <div className="space-y-3">
                    {scheduledPosts.map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{post.title}</p>
                          <p className="text-xs text-slate-500">
                            {post.publish_date ? new Date(post.publish_date).toLocaleDateString('en-AU', { 
                              weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                            }) : 'Not scheduled'}
                          </p>
                        </div>
                        <button onClick={() => openEditDialog(post.id)} className="text-slate-600 hover:text-slate-900">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No scheduled posts</p>
                )}
              </div>

              {/* Recently Published */}
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Recently Published
                </h3>
                {stats?.recent_posts?.filter(p => p.status === 'published').length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_posts.filter(p => p.status === 'published').slice(0, 5).map((post) => (
                      <div key={post.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{post.title}</p>
                          <p className="text-xs text-slate-500">
                            {new Date(post.created_at).toLocaleDateString('en-AU')}
                          </p>
                        </div>
                        <a href={`/blog/${post.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No published posts yet</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* AI Workflow Tab */}
          <TabsContent value="ai-workflow" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-3">
              {/* Left Column - Topic Generation */}
              <div className="lg:col-span-2 space-y-6">
                {/* Category & Custom Topic */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Generate Blog Post</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Category</Label>
                      <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Custom Topic</Label>
                      <div className="flex gap-2">
                        <Input
                          value={customTopic}
                          onChange={(e) => setCustomTopic(e.target.value)}
                          placeholder="Enter your own topic..."
                        />
                        <Button 
                          onClick={() => {
                            if (customTopic.trim()) {
                              generateSinglePost(customTopic);
                              setCustomTopic("");
                            }
                          }}
                          disabled={generating || !customTopic.trim()}
                        >
                          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Suggested Topics */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      AI Suggested Topics
                    </h3>
                    <Button variant="outline" size="sm" onClick={fetchTopics} disabled={loadingTopics}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingTopics ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  {loadingTopics ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {suggestedTopics.map((topic, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group hover:bg-slate-100 transition-colors">
                          <span className="text-sm text-slate-700">{topic}</span>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => generateSinglePost(topic)}
                              disabled={generating}
                              className="text-amber-600 hover:text-amber-700"
                              title="Generate now"
                            >
                              <Wand2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => addToBatch(topic)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Add to batch"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Industry News */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Newspaper className="w-5 h-5 text-blue-500" />
                      Latest Industry News
                    </h3>
                    <Button variant="outline" size="sm" onClick={fetchNews} disabled={loadingNews}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingNews ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  {loadingNews ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {newsItems.slice(0, 6).map((news, index) => (
                        <a
                          key={index}
                          href={news.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <p className="text-sm font-medium text-slate-900 line-clamp-2">{news.title}</p>
                          <p className="text-xs text-slate-500 mt-1">{news.source}</p>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Batch Queue */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-slate-200 p-6 sticky top-24">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    Batch Queue ({batchTopics.length})
                  </h3>
                  
                  {batchTopics.length > 0 ? (
                    <>
                      <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                        {batchTopics.map((topic, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                            <span className="line-clamp-1">{topic}</span>
                            <button onClick={() => removeFromBatch(topic)} className="text-red-500 hover:text-red-600">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      <Button 
                        onClick={generateBatchPosts}
                        disabled={generating}
                        className="w-full"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate {batchTopics.length} Posts
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-slate-500 mt-2 text-center">
                        Posts will be scheduled for Mon-Fri at 8am
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Add topics from the suggestions or enter custom topics
                    </p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {stats ? (
              <>
                {/* Stat Cards */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Posts</p>
                        <p className="text-3xl font-bold text-slate-900">{stats.total_posts}</p>
                      </div>
                      <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Published</p>
                        <p className="text-3xl font-bold text-green-600">{stats.published_posts}</p>
                      </div>
                      <CheckCircle2 className="w-8 h-8 text-green-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Scheduled</p>
                        <p className="text-3xl font-bold text-amber-600">{stats.scheduled_posts}</p>
                      </div>
                      <Clock className="w-8 h-8 text-amber-400" />
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Views</p>
                        <p className="text-3xl font-bold text-blue-600">{stats.total_views}</p>
                      </div>
                      <Eye className="w-8 h-8 text-blue-400" />
                    </div>
                  </div>
                </div>

                {/* Top Posts & Categories */}
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      Top Performing Posts
                    </h3>
                    {stats.top_posts?.length > 0 ? (
                      <div className="space-y-3">
                        {stats.top_posts.map((post, index) => (
                          <div key={post.id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-lg font-bold text-slate-400">#{index + 1}</span>
                              <span className="text-sm text-slate-700">{post.title}</span>
                            </div>
                            <span className="text-sm font-medium text-slate-900">{post.views} views</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No posts yet</p>
                    )}
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <h3 className="font-semibold text-slate-900 mb-4">Posts by Category</h3>
                    {Object.keys(stats.posts_by_category || {}).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(stats.posts_by_category).map(([category, count]) => (
                          <div key={category}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-700">{category}</span>
                              <span className="font-medium text-slate-900">{count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full"
                                style={{ width: `${(count / stats.total_posts) * 100}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No categories yet</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Blog Post</DialogTitle>
          </DialogHeader>
          
          {editingPost && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editingPost.title}
                  onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Excerpt / Meta Description</Label>
                <Textarea
                  value={editingPost.excerpt}
                  onChange={(e) => setEditingPost({ ...editingPost, excerpt: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Category</Label>
                  <Select 
                    value={editingPost.category} 
                    onValueChange={(v) => setEditingPost({ ...editingPost, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={editingPost.status} 
                    onValueChange={(v) => setEditingPost({ ...editingPost, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Content (Markdown)</Label>
                <Textarea
                  value={editingPost.content}
                  onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
              
              {/* Featured Image Section */}
              <div>
                <Label>Featured Image</Label>
                <div className="mt-2 space-y-4">
                  {/* Current Image Preview */}
                  {editingPost.featured_image_url && (
                    <div className="relative">
                      <img 
                        src={editingPost.featured_image_url.startsWith('/api') ? `${BACKEND_URL}${editingPost.featured_image_url}` : editingPost.featured_image_url} 
                        alt="Featured" 
                        className="w-full max-w-md rounded-lg border border-slate-200"
                      />
                      {editingPost.image_approved && (
                        <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                          <Check className="w-3 h-3" /> Approved
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Image Actions */}
                  <div className="flex flex-wrap gap-2">
                    {/* Upload Button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => handleFileSelect(e, editingPost.id)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Image
                        </>
                      )}
                    </Button>
                    
                    {/* Regenerate AI Image */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => regenerateImage(editingPost.id)}
                      disabled={uploadingImage}
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Generate AI Image
                    </Button>
                    
                    {/* Approve Image */}
                    {editingPost.featured_image_url && !editingPost.image_approved && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => approveImage(editingPost.id)}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve Image
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-slate-500">
                    Upload your own image (JPEG, PNG, WebP, GIF) or generate one with AI.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={savePost}>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
